import React, { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { connectRedux } from '../../helpers/connectRedux'
import { RouteComponentProps, withRouter } from '../../helpers/withRouter'
import {
  AppDownload,
  ContactFooter,
  FrequentlyAskedQuestions,
  LandingNavigation,
  Page,
  SlideDirection,
  SlideTransition,
  Title
} from '../../components'
import { images } from '../../images'
import { ILandingLink, ISignInLanding } from '../../models'
import GoogleSignInButton, { isGoogleSignInAvailable } from '../../containers/GoogleSignInButton'
import LanguageSelector from '../../containers/LanguageSelector'
import SignInForm from '../../containers/SignInForm'
import { urls } from '../../routing'
import { LiteralsService } from '../../services'
import {
  clearSignInFieldsAction,
  clearSignUpFieldsAction,
  clearVerificationFieldsAction,
  resetRecoverPasswordFieldsAction,
  setGuestModeAction
} from '../../store/actions'
import { IStoreState } from '../../store/states'
import StyledArticle, { StyledClassicArticle, StyledLandingArticle } from './styles'
import NewPasswordRequired from '../NewPasswordRequired'
import { RecoverPasswordRequestCodeStep, RecoverPasswordRestoreStep } from '../RecoverPassword'
import { SignUpStep1, SignUpStep2, SignUpStep3 } from '../SignUp'
import { AccountVerificationResend, AccountVerificationStep } from '../Verification'
import {
  appsGeneralInfoSelected,
  appsLogosSelected,
  appsStyleSelected,
  isClassicSignIn,
  signInLandingSelected
} from '../../helpers/app'

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  language: string
}

// The form itself lives in SignInForm, shared with the prompt shown to guests.
class SignIn extends PureComponent<IProps> {
  private landingLinks: ILandingLink[] = [
    { href: `${ urls.signIn }#frequently-asked-questions`, labelKey: 'frequentlyAskedQuestions' },
    { href: `${ urls.signIn }#contact`, labelKey: 'contactSectionTitle' },
  ]

  public componentDidMount() {
    this.updateLandingTitle()
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps.language !== this.props.language) this.updateLandingTitle()
  }

  public componentWillUnmount() {
    if (signInLandingSelected) document.title = process.env.REACT_APP_title ?? ''
  }

  private updateLandingTitle() {
    if (!signInLandingSelected) return

    document.title = `${ LiteralsService.get('landingPageTitle', true) } · ${ appsGeneralInfoSelected?.title }`
  }

  public render(): ReactNode {
    if (isClassicSignIn) return this.classic
    if (signInLandingSelected) return this.renderLanding(signInLandingSelected)

    return (
      <Page className={ `${appsStyleSelected?.style}` }>
        <StyledArticle>
          <div className="language-selector">
            <LanguageSelector />
          </div>
          <form onSubmit={ e => e.preventDefault() }>
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
              <h2>{ LiteralsService.get('signIn', true) }</h2>
            </header>
            { isGoogleSignInAvailable &&
              <section className="federated">
                <GoogleSignInButton />
                <div className="divider">
                  { LiteralsService.get('orWithYourAccount') }
                </div>
              </section>
            }
            <SignInForm
              onForgotPassword={ this.goToRecoverPassword }
              onVerification={ this.goToVerification }
              onSignUp={ this.goToSignUp }
              onGuest={ this.goToGuestMode }
            />
          </form>
        </StyledArticle>
      </Page>
    )
  }

  // The pre-redesign screen, for the brands that keep it (see appsSignInLayout).
  private get classic(): ReactNode {
    return (
      <Page className={ `${appsStyleSelected?.style}` }>
        <StyledClassicArticle>
          <form onSubmit={ e => e.preventDefault() }>
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
            </header>
            <Title text={ LiteralsService.get('signIn', true) }/>
            <SignInForm
              classic
              onForgotPassword={ this.goToRecoverPassword }
              onVerification={ this.goToVerification }
              onSignUp={ this.goToSignUp }
              onGuest={ this.goToGuestMode }
            />
          </form>
        </StyledClassicArticle>
      </Page>
    )
  }

  private renderLandingPanel = (pathname: string): ReactNode => {
    switch (pathname) {
      case urls.signUpStep1: return <SignUpStep1 embedded />
      case urls.signUpStep2: return <SignUpStep2 embedded />
      case urls.signUpStep3: return <SignUpStep3 embedded />
      case urls.recoverPasswordRequestCodeStep: return <RecoverPasswordRequestCodeStep embedded />
      case urls.recoverPasswordRestoreStep: return <RecoverPasswordRestoreStep embedded />
      case urls.accountVerificationStep: return <AccountVerificationStep embedded />
      case urls.accountVerificationResend: return <AccountVerificationResend embedded />
      case urls.newPasswordRequired: return <NewPasswordRequired embedded />
      default: return this.landingSignInForm
    }
  }

  private get landingSignInForm(): ReactNode {
    return (
      <form onSubmit={ this.preventSubmit }>
        <header>
          <img  className={ appsLogosSelected?.classRounded }
                src={ appsLogosSelected?.logoRounded }
                alt={ appsLogosSelected?.alt } />
          <h2>{ LiteralsService.get('signInToGetStarted', true) }</h2>
        </header>
        <SignInForm
          onForgotPassword={ this.goToRecoverPassword }
          onVerification={ this.goToVerification }
          onSignUp={ this.goToSignUp }
          onGuest={ this.goToGuestMode }
        />
      </form>
    )
  }

  private getPanelDepth(pathname: string): number {
    switch (pathname) {
      case urls.signUpStep1:
      case urls.recoverPasswordRequestCodeStep:
      case urls.newPasswordRequired:
        return 1
      case urls.signUpStep2:
      case urls.recoverPasswordRestoreStep:
        return 2
      case urls.signUpStep3:
        return 3
      case urls.accountVerificationStep:
        return 4
      case urls.accountVerificationResend:
        return 5
      default:
        return 0
    }
  }

  private getPanelDirection = (previousPathname: string, nextPathname: string): SlideDirection =>
    this.getPanelDepth(nextPathname) >= this.getPanelDepth(previousPathname) ? 'forward' : 'back'

  private renderLanding(landing: ISignInLanding): ReactNode {
    const { language, location } = this.props

    return (
      <Page className={ `${appsStyleSelected?.style} landing` }>
        <StyledLandingArticle $heroImage={ images[landing.heroImage] }>
          <main>
            <section className="hero" aria-labelledby="landing-title">
              <div className="hero-header">
                <img className="hero-logo" src={ images[landing.heroLogo] } alt={ appsLogosSelected?.alt } />
                <div className="hero-actions">
                  <LandingNavigation links={ this.landingLinks } language={ language } />
                  <div className="language-selector">
                    <LanguageSelector />
                  </div>
                </div>
              </div>
              <div className="hero-content">
                <h1 id="landing-title">
                  { LiteralsService.get('landingTitle', true) }{ ' ' }
                  <span className="highlight">{ LiteralsService.get('landingTitleHighlight') }</span>
                </h1>
                <p className="subtitle">{ LiteralsService.get('landingSubtitle', true) }</p>
                <AppDownload
                  className="hero-download"
                  label={ LiteralsService.get('landingDownloadApp', true) }
                  stores={ landing.stores }
                  showQrCodes
                />
              </div>
            </section>
            <section className="access">
              <SlideTransition
                panelKey={ location.pathname }
                getDirection={ this.getPanelDirection }
                renderPanel={ this.renderLandingPanel }
              />
              <AppDownload
                className="access-download"
                label={ LiteralsService.get('landingSignInFromApp', true) }
                stores={ landing.stores }
              />
            </section>
          </main>
          <FrequentlyAskedQuestions
            numberOfQuestions={ landing.frequentlyAskedQuestions }
            contact={ landing.contact }
            language={ language }
          />
          <ContactFooter contact={ landing.contact } partnerLogos={ landing.partnerLogos } language={ language } />
        </StyledLandingArticle>
      </Page>
    )
  }

  private preventSubmit = (event: React.FormEvent<HTMLFormElement>) => event.preventDefault()

  private goToRecoverPassword = () => {
    const { dispatch, history } = this.props

    dispatch(resetRecoverPasswordFieldsAction())
    history.push(urls.recoverPasswordRequestCodeStep)
  }

  private goToSignUp = () => {
    const { dispatch, history  } = this.props

    dispatch(clearSignUpFieldsAction())
    dispatch(clearSignInFieldsAction())
    history.push(urls.signUpStep1)
  }

  // An account that was created but never verified: the same screen the sign up ends in.
  private goToVerification = () => {
    const { dispatch, history } = this.props

    dispatch(clearVerificationFieldsAction())
    history.push(urls.accountVerificationStep)
  }

  private goToGuestMode = () => {
    const { dispatch, history } = this.props

    dispatch(setGuestModeAction())
    history.push(urls.newReservationBusStopsStep)
  }
}

// Redraws the literals when the language selector changes the language.
const mapStateToProps = ({ configuration }: IStoreState) => ({
  language: configuration.language
})

export default withRouter(connectRedux(mapStateToProps, SignIn))
